/**
 * Captions the backend prints in the inspection report (`app/utils/locale/common/pl.js`). Their length decides
 * line wrapping, so the estimate measures the Polish copy — the reference locale the estimator is calibrated on.
 */
export const INSPECTION_PDF_LABELS = {
  reportTitle: 'Raport z inspekcji',
  reportMessage: 'Komentarz do raportu',
  reportCreatedBy: 'Osoba generująca raport',
  creationDate: 'Data utworzenia',
  inspectionDataHeading: 'Dane inspekcji',
  inspectionName: 'Nazwa inspekcji',
  inspectionNumber: 'Numer inspekcji',
  executionDate: 'Data wykonania',
  status: 'Status',
  completedStatus: 'Zakończona',
  site: 'Lokalizacja',
  level: 'Podlokalizacja',
  controlledParty: 'Strona kontrolowana',
  contractNumber: 'Numer umowy',
  workType: 'Typ prac',
  inspectedBy: 'Skontrolowane przez',
  comment: 'Komentarz',
  score: 'Wynik inspekcji',
  generalStandardHeading: 'Ogólnie stosowany wzorzec',
  standardDescription: 'Opis wzorca',
  generalStandardAttachments: 'Załączniki do wzorca',
  summaryHeading: 'Podsumowanie raportu z inspekcji',
  summaryColumns: [
    'Elementy kontrolowane',
    'Zgodny',
    'Niezgodny',
    'Nie dotyczy',
    'Niesprawdzony',
    'Pozostałe odpowiedzi',
    'Zgłoszenia',
  ],
  scoreFootnote:
    'Wynik inspekcji = Zgodne / (Zgodne + Niezgodne). Nie dotyczy, Niesprawdzone i Pozostałe odpowiedzi nie wchodzą do mianownika.',
  checklistHeading: 'Lista kontrolna',
  checklistColumns: ['Lp.', 'Element kontrolowany', 'Wynik'],
  sectionDescription: 'Opis sekcji',
  sectionAttachments: 'Załączniki sekcji',
  sectionScore: 'wynik sekcji:',
  sectionComment: 'Komentarz do sekcji',
  sectionCommentAttachments: 'Załączniki komentarza do sekcji',
  appliedStandard: 'Zastosowany wzorzec',
  standardAttachments: 'Załączniki do normy',
  answerAttachments: 'Załączniki do odpowiedzi',
  notApplicable: 'Nie dotyczy',
  passed: 'Zgodny',
  notPassed: 'Niezgodny',
  unchecked: 'Niesprawdzony',
  outOfNorm: 'POZA NORMĄ',
  issueShort: 'zgłoszenie',
  registerPosition: 'Poz.',
  issueRegisterHeading: 'Rejestr zgłoszeń',
  relatedChecklistItem: 'Powiązany element listy kontrolnej',
  stage: 'Etap',
  issueTitle: 'Podsumowanie',
  issueDescription: 'Opis',
  issueAttachments: 'Załączniki do zgłoszenia',
  contactsHeading: 'Dane osobowe uczestników',
  contactsUser: 'Użytkownik',
  contactsEmail: 'E-mail',
  contactsPhone: 'Telefon',
  signatureColumns: ['ZATWIERDZONO PRZEZ', 'PODPIS', 'DATA'],
  fileName: 'Nazwa pliku',
  fileDescription: 'Opis',
  downloadLink: 'link',
  filePlaceholder: (index: number, extension: string): string =>
    `plik ${index}.${extension}`,
} as const;

/**
 * Stand-ins for values the frontend does not hold in the shape the PDF prints them — issue codes, dates,
 * dictionary labels of issue fields. They only have to be as long as the typical real value.
 */
export const INSPECTION_PDF_SAMPLE_VALUES = {
  date: '14/05/2024',
  inspectionNumber: '2024/IN/QA/0042',
  issueCode: '2024/QA/A/0000',
  shortFieldValue: 'Wartość pola',
  userName: 'Jan Kowalski',
  userEmail: 'jan.kowalski@firma.pl',
  userPhone: '+48 600 100 200',
  scorePercent: '72,2%',
  issueStage: 'W realizacji',
  imageFileName: 'zdjecie_0000_0.jpg',
  /** Height-to-width ratio of a picture; real proportions are unknown before the upload is read. */
  imageAspectRatio: 0.75,
} as const;

/** `1` + `Lista kontrolna` -> `1. Lista kontrolna` (`formatSectionHeading`). */
export function formatInspectionSectionHeading(
  sectionNumber: number,
  label: string
): string {
  return `${sectionNumber}. ${label}`;
}
